import auth from "shared/Auth0";

const bearerToken = (headers: Headers) =>
  headers.get("Authorization")?.replace(/^Bearer /, "") ?? "";

/**
 * fetch wrapper for the NSwag ApiClient. On a 401, silently refreshes the
 * Auth0 session (shared with any concurrent callers) and retries once with
 * the new token — mirroring the axios-auth-refresh behavior in services/axios.
 */
export const authFetch = async (
  ...[url, init]: Parameters<typeof fetch>
): Promise<Response> => {
  const response = await fetch(url, init);
  if (response.status !== 401) {
    return response;
  }

  const headers = new Headers(init?.headers);
  const staleToken = bearerToken(headers);

  // Skip the refresh if another caller already renewed the token while this request was in flight
  if (auth.getAccessToken() === staleToken) {
    try {
      await auth.refreshSession();
    } catch (err) {
      // Transient failures just return the 401; the renewal timer retries
      if (auth.isSessionLost(err)) {
        window.location.href = `${window.location.origin}/logout`;
      }
      return response;
    }
  }

  const freshToken = auth.getAccessToken();
  headers.set("Authorization", `Bearer ${freshToken}`);

  // Some endpoints (e.g. getScreenInvites, updateScreenFromInvite) also carry the
  // token in their JSON body, so swap it there too
  const body =
    staleToken && typeof init?.body === "string"
      ? init.body.split(staleToken).join(freshToken)
      : init?.body;

  return fetch(url, { ...init, headers, body });
};
